import { useState, useRef, useCallback, useEffect } from 'react';
import { DRAG_THRESHOLD, DRAG_CLEAR_DELAY } from '../constants/layout';

/**
 * Custom hook for drag-and-drop interactions with threshold detection.
 * Prevents accidental drags by requiring mouse movement beyond a threshold.
 * Manages click prevention after drag operations.
 * 
 * @param {Object} config
 * @param {number} [config.threshold=DRAG_THRESHOLD] - Pixels to move before drag activates
 * @param {Function} config.onDragStart - Callback when drag starts: (dragData, mouseEvent) => void
 * @param {Function} [config.onDragEnd] - Callback when drag ends (mouseup anywhere): () => void
 * @param {Function} config.buildDragData - Function to build drag data: (mouseEvent) => dragData
 * @returns {Object} Drag state and handlers
 * @returns {boolean} returns.isDragging - Whether currently dragging
 * @returns {React.RefObject} returns.wasDraggingRef - Ref tracking recent drag (for click prevention)
 * @returns {Function} returns.handlePotentialDrag - Call on mousedown: (e) => void
 */
function useDraggable({ 
  threshold = DRAG_THRESHOLD, 
  onDragStart,
  onDragEnd,
  buildDragData
}) {
  const [isDragging, setIsDragging] = useState(false);
  const wasDraggingRef = useRef(false);
  const potentialDragRef = useRef(null);

  // Start actual drag after threshold is crossed
  const startDrag = useCallback((dragData, mouseEvent) => {
    setIsDragging(true);
    wasDraggingRef.current = true;
    
    if (onDragStart) {
      onDragStart(dragData, mouseEvent);
    }
  }, [onDragStart]);

  // Check if mouse has moved beyond threshold
  const checkDragThreshold = useCallback((e) => {
    if (!potentialDragRef.current) return;
    
    const { startX, startY, dragData, mouseEvent, mouseMoveHandler, mouseUpHandler } = potentialDragRef.current;
    const deltaX = Math.abs(e.clientX - startX);
    const deltaY = Math.abs(e.clientY - startY);
    
    // If moved beyond threshold, activate drag
    if (deltaX > threshold || deltaY > threshold) {
      potentialDragRef.current = null;
      // Remove both listeners since drag is now active
      if (mouseMoveHandler) {
        window.removeEventListener('mousemove', mouseMoveHandler);
      }
      if (mouseUpHandler) {
        window.removeEventListener('mouseup', mouseUpHandler);
      }
      startDrag(dragData, mouseEvent);
    }
  }, [threshold, startDrag]);

  // Prepare for potential drag on mousedown
  const handlePotentialDrag = useCallback((e) => {
    if (e.button !== 0) return; // Only left click
    
    // Build drag data using provided function
    const dragData = buildDragData ? buildDragData(e) : {};
    
    // If buildDragData returned null, don't start drag (e.g., locked item)
    if (!dragData) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    // Create the mousemove handler
    const mouseMoveHandler = (moveEvent) => checkDragThreshold(moveEvent);
    
    // Create the mouseup handler to clean up if drag never activates
    const mouseUpHandler = () => {
      // Clean up potential drag
      if (potentialDragRef.current) {
        window.removeEventListener('mousemove', mouseMoveHandler);
        window.removeEventListener('mouseup', mouseUpHandler);
        potentialDragRef.current = null;
        // This was just a click, don't set wasDraggingRef
      }
    };
    
    // Store potential drag info
    potentialDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      dragData,
      mouseEvent: e,
      mouseMoveHandler,
      mouseUpHandler
    };
    
    // Add global listeners to detect threshold crossing or cleanup on mouseup
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);
  }, [buildDragData, checkDragThreshold]);

  // Reset isDragging state when a global mouseup occurs during active drag
  useEffect(() => {
    if (!isDragging) return;
    
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      // Call onDragEnd if provided (allows drop at last valid position)
      if (onDragEnd) {
        onDragEnd();
      }
      // Keep wasDraggingRef true for a short time so click doesn't fire
      setTimeout(() => {
        wasDraggingRef.current = false;
      }, DRAG_CLEAR_DELAY);
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, onDragEnd]);

  return {
    isDragging,
    wasDraggingRef,
    handlePotentialDrag
  };
}

export default useDraggable;
